import { Button } from "@component/button";
import { useRef } from "react";
import {
    XIcon,
    ChevronDownIcon,
    Trash2Icon,
    Loader2Icon,
} from "lucide-react";
import { useSubscription } from "@context/subscription_context";
import { useMaintenanceGate } from "@context/maintenance";
import {
    Tooltip,
    TooltipProvider,
} from "@component/tooltip";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from "@component/dropdown-menu";

type FunnelStage = "applied" | "pending_assessment" | "to_review" | "closed";

interface BulkApplicationActionsBarProps {
    applicants: Applicant[];
    selectedCount: number;
    onClearSelection: () => void;
    currentStage: string;
    selectedApplicantsStages: Set<FunnelStage>;
    onSendInterview?: () => void;
    onHire?: () => void;
    onReject?: () => void;
    /** Reject the selected candidates over WhatsApp — only offered for WhatsApp-first hiring-flow jobs. */
    onRejectByWa?: () => void;
    /** The job runs a WhatsApp-first hiring flow — enables the "Reject by WhatsApp" option. */
    isWaHiringFlow?: boolean;
    onSendAssessment?: () => void;
    /** Send the screening invitation over WhatsApp (#3478) — opens the preview dialog. */
    onSendAssessmentViaWhatsapp?: () => void;
    onSendAssessmentReminder?: () => void;
    onResendInvitation?: () => void;
    onRequestRetake?: () => void;
    selectedApplicantsStatuses?: Set<string>; // Set of all selected applicants' statuses
    assessmentReminderCount?: number; // Count of candidates eligible for assessment reminder
    interviewRetakeCount?: number; // Count of candidates eligible for interview retake
    onMarkAsHired?: () => void; // Callback for marking candidates as hired without email
    onMarkAsOfferSent?: () => void; // Callback for marking candidates as offer sent without email
    onMarkAsReviewScheduled?: () => void; // Callback for marking as recruiter interview scheduled
    onMarkAsPendingDecision?: () => void; // Callback for marking as pending decision
    onMarkAsOfferResent?: () => void; // Callback for marking candidates as offer resent without email
    onMarkAsOfferDeclined?: () => void; // Callback for marking candidates as offer declined without email
    onMarkAsArchived?: () => void; // Callback for marking candidates as archived
    onMarkAsMoved?: () => void; // Callback for marking candidates as moved to another stage
    onMarkAsShortlisted?: () => void;
    onMarkAsOnHold?: () => void;
    onMarkAsNotSuitable?: () => void;
    onDelete?: () => void; // Callback for deleting selected candidates
    isDeleting?: boolean; // Whether a delete request is currently in progress
}


// Debounce utility that always uses the latest function reference
import { useCallback } from "react";
import { Applicant } from "@type/applicant";

let debounceTimeoutId: ReturnType<typeof setTimeout> | undefined;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function useDebouncedCallback<T extends (...args: any[]) => void>(fn: T | undefined, delay = 400) {
    const fnRef = useRef(fn);
    fnRef.current = fn;
    return useCallback((...args: Parameters<T>) => {
        if (!fnRef.current) return;
        if (debounceTimeoutId) clearTimeout(debounceTimeoutId);
        debounceTimeoutId = setTimeout(() => fnRef.current && fnRef.current(...args), delay);
    }, [delay]);
}

export default function BulkApplicationActionsBar({
    applicants = [],
    selectedCount,
    onClearSelection,
    currentStage,
    selectedApplicantsStages,
    onSendInterview,
    onHire,
    onReject,
    onRejectByWa,
    isWaHiringFlow,
    onSendAssessment,
    onSendAssessmentViaWhatsapp,
    onSendAssessmentReminder,
    onRequestRetake,
    onResendInvitation,
    selectedApplicantsStatuses,
    assessmentReminderCount = 0,
    interviewRetakeCount = 0,
    onMarkAsHired,
    onMarkAsOfferSent,
    onMarkAsReviewScheduled,
    onMarkAsPendingDecision,
    onMarkAsOfferResent,
    onMarkAsOfferDeclined,
    onMarkAsArchived,
    onMarkAsMoved,
    onMarkAsShortlisted,
    onMarkAsOnHold,
    onMarkAsNotSuitable,
    onDelete,
    isDeleting = false,
}: BulkApplicationActionsBarProps) {
    // `isWorkspaceRestricted` disables the whole bar after the trial grace
    // window. Assessment-consuming actions (send invitation, reminder, resend,
    // retake) are additionally blocked when the AI-assessment cap is hit — via
    // `isAssessmentActionBlocked`. A CV cap alone does NOT block these.
    const { isWorkspaceRestricted, isAssessmentActionBlocked } = useSubscription();
    // Maintenance dims the same action row trial-expiry does, but for its own
    // reason and with its own wording — see the wrapper below.
    const maintenance = useMaintenanceGate();

    // Check if candidates are from multiple different stages
    const isMultiStageSelection = selectedApplicantsStages
        ? selectedApplicantsStages.size > 1
        : false;

    // Check if all selected candidates have "assessment_completed" status
    const allHaveCompletedAssessment = selectedApplicantsStatuses
        ? Array.from(selectedApplicantsStatuses).every(
            (status) => status === "Assessment Completed"
        )
        : false;

    const isNotLinkExpired = selectedApplicantsStatuses
        ? Array.from(selectedApplicantsStatuses).every(
            (status) => status !== "Link Expired"
        )
        : false;

    // Determine which actions should be disabled based on eligible candidate counts
    const isReminderDisabled = assessmentReminderCount === 0;
    const isRetakeDisabled = interviewRetakeCount === 0;

    // Calculate resend invitation count from statuses
    const resendInvitationStatuses = ["Link Expired"];
    const resendInvitationCount = selectedApplicantsStatuses
        ? Array.from(selectedApplicantsStatuses).filter((status) =>
            resendInvitationStatuses.includes(status)
        ).length
        : 0;
    const isResendInvitationDisabled = resendInvitationCount === 0;

    // Check if any selected applicant has no email
    const hasApplicantWithoutEmail = applicants.some(
        (applicant) => !applicant.main_email || applicant.main_email.trim() === ""
    );

    // #3478: bulk-uploaded candidates often carry a CV-parsed phone number and no
    // email, so the invitation can go over WhatsApp instead. Presence check only —
    // the BE preview (#4533) is authoritative on validity and returns a per-candidate
    // reason (missing/invalid number, already invited, WhatsApp off).
    const hasApplicantWithoutPhone = applicants.some(
        (applicant) => !applicant.phone_number || applicant.phone_number.trim() === ""
    );

    // Debounced handlers for dropdown menu items (always latest prop)
    const debouncedOnSendAssessmentViaWhatsapp = useDebouncedCallback(onSendAssessmentViaWhatsapp);
    const debouncedOnResendInvitation = useDebouncedCallback(onResendInvitation);
    const debouncedOnSendAssessmentReminder = useDebouncedCallback(onSendAssessmentReminder);
    const debouncedOnRequestRetake = useDebouncedCallback(onRequestRetake);
    const debouncedOnHire = useDebouncedCallback(onHire);
    const debouncedOnMarkAsOfferSent = useDebouncedCallback(onMarkAsOfferSent);
    const debouncedOnMarkAsHired = useDebouncedCallback(onMarkAsHired);
    const debouncedOnMarkAsOfferResent = useDebouncedCallback(onMarkAsOfferResent);
    const debouncedOnMarkAsOfferDeclined = useDebouncedCallback(onMarkAsOfferDeclined);
    const debouncedOnSendInterview = useDebouncedCallback(onSendInterview);
    const debouncedOnMarkAsReviewScheduled = useDebouncedCallback(onMarkAsReviewScheduled);
    const debouncedOnMarkAsPendingDecision = useDebouncedCallback(onMarkAsPendingDecision);
    const debouncedOnReject = useDebouncedCallback(onReject);
    const debouncedOnRejectByWa = useDebouncedCallback(onRejectByWa);
    const debouncedOnMarkAsArchived = useDebouncedCallback(onMarkAsArchived);
    const debouncedOnMarkAsMoved = useDebouncedCallback(onMarkAsMoved);
    const debouncedOnMarkAsShortlisted = useDebouncedCallback(onMarkAsShortlisted);
    const debouncedOnMarkAsOnHold = useDebouncedCallback(onMarkAsOnHold);
    const debouncedOnMarkAsNotSuitable = useDebouncedCallback(onMarkAsNotSuitable);

    // Render CTA buttons based on current stage
    const renderCTAButtons = () => {
        const suffix = selectedCount === 1 ? "" : "s";
        const allSelectedHaveStatus = (status: string) =>
            selectedApplicantsStatuses?.size === 1 && selectedApplicantsStatuses.has(status);

        // If multi-stage selection, only show Close button
        if (isMultiStageSelection) {
            return (
                <Tooltip content={(
                    <p>Reject candidates and move them to the Closed stage.</p>
                )}>
                    <Button
                        variant="default"
                        size="sm"
                        onClick={debouncedOnReject}
                        className="gap-2 bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 text-white font-medium transition-all h-8 px-4 rounded-md shadow-sm"
                    >
                        Reject Candidate{suffix}
                        <ChevronDownIcon className="w-4 h-4" />
                    </Button>
                </Tooltip>
            );
        }

        // Render buttons based on current stage
        switch (currentStage) {
            case "uploads":
            case "applied":
                return (
                    <>
                        {/* Update Candidate Status - outline, left */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="gap-2 bg-white hover:bg-gray-100 dark:bg-transparent dark:hover:bg-gray-800 text-black dark:text-gray-100 font-medium transition-all h-8 px-4 rounded-md border border-gray-300 dark:border-gray-600"
                                >
                                    Update Candidate Status
                                    <ChevronDownIcon className="w-4 h-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-72">
                                <DropdownMenuLabel className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-3 pt-3 pb-1">Within This Stage</DropdownMenuLabel>
                                {!allSelectedHaveStatus("Shortlisted") && (
                                    <DropdownMenuItem
                                        onClick={debouncedOnMarkAsShortlisted}
                                        className="flex items-start gap-3 p-3 cursor-pointer"
                                    >
                                        <div className="flex-1">
                                            <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as Shortlisted</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">CV looks good. Ready to send a screening invitation.</div>
                                        </div>
                                    </DropdownMenuItem>
                                )}
                                {!allSelectedHaveStatus("On Hold") && (
                                    <DropdownMenuItem
                                        onClick={debouncedOnMarkAsOnHold}
                                        className="flex items-start gap-3 p-3 cursor-pointer"
                                    >
                                        <div className="flex-1">
                                            <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as On Hold</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Not the right time to progress. Candidate is kept for future consideration.</div>
                                        </div>
                                    </DropdownMenuItem>
                                )}
                                {!allSelectedHaveStatus("Not Suitable") && (
                                    <DropdownMenuItem
                                        onClick={debouncedOnMarkAsNotSuitable}
                                        className="flex items-start gap-3 p-3 cursor-pointer"
                                    >
                                        <div className="flex-1">
                                            <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as Not Suitable</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Does not meet requirements for this role.</div>
                                        </div>
                                    </DropdownMenuItem>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                        {/* Send Screening Invitation - blue, middle. #3478: ONE button; the
                            hiring flow decides the channel. A whatsapp_first flow sends on
                            WhatsApp (its candidates usually have a phone number and no email);
                            web flows and legacy jobs keep the email invitation exactly as
                            before. No channel picker — HR never has to choose. */}
                        <Tooltip content={
                            isWaHiringFlow
                                ? (hasApplicantWithoutPhone
                                    ? <p>Some selected candidates do not have a phone number.</p>
                                    : <p>Send the screening link to selected candidates on WhatsApp.</p>)
                                : (hasApplicantWithoutEmail
                                    ? <p>Some selected candidates do not have an email address.</p>
                                    : <p>Send screening links to selected candidates.</p>)
                        }>
                            <Button
                                variant="default"
                                size="sm"
                                onClick={isWaHiringFlow ? debouncedOnSendAssessmentViaWhatsapp : onSendAssessment}
                                disabled={isAssessmentActionBlocked || (isWaHiringFlow ? hasApplicantWithoutPhone : hasApplicantWithoutEmail)}
                                className="gap-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium transition-all h-8 px-4 rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Send Screening Invitation
                            </Button>
                        </Tooltip>
                        {/* Reject Candidate - red, right */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="default"
                                    size="sm"
                                    className="gap-2 bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 text-white font-medium transition-all h-8 px-4 rounded-md shadow-sm"
                                >
                                    Reject Candidate{suffix}
                                    <ChevronDownIcon className="w-4 h-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-72">
                                <DropdownMenuItem
                                    onClick={debouncedOnReject}
                                    disabled={hasApplicantWithoutEmail}
                                    className="flex items-start gap-3 p-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <div className="flex-1">
                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                            Send Rejection Email
                                        </div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            {hasApplicantWithoutEmail
                                                ? "Some selected candidates do not have an email address."
                                                : "Notify candidates they were not selected for this role. Candidate moves to the Closed stage."}
                                        </div>
                                    </div>
                                </DropdownMenuItem>
                                {isWaHiringFlow && (
                                    <DropdownMenuItem
                                        onClick={debouncedOnRejectByWa}
                                        className="flex items-start gap-3 p-3 cursor-pointer"
                                    >
                                        <div className="flex-1">
                                            <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Reject by WhatsApp</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Notify candidates over WhatsApp using the approved rejection template. Candidates move to the Closed stage.</div>
                                        </div>
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                    onClick={debouncedOnMarkAsArchived}
                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                >
                                    <div className="flex-1">
                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                            Mark as Archived
                                        </div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            Candidate moves to the Closed stage without email notification.
                                        </div>
                                    </div>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </>
                );

            case "pending_assessment":
                return (
                    <>
                        {!allHaveCompletedAssessment && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="default"
                                        size="sm"
                                        disabled={hasApplicantWithoutEmail}
                                        title={hasApplicantWithoutEmail ? "Some selected candidates do not have an email address." : undefined}
                                        className="gap-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium transition-all h-8 px-4 rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Email Candidate{suffix}
                                        <ChevronDownIcon className="w-4 h-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-72">
                                    <DropdownMenuItem
                                        onClick={debouncedOnSendAssessmentReminder}
                                        disabled={isReminderDisabled || isAssessmentActionBlocked}
                                        className="flex items-start gap-3 p-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <div className="flex-1">
                                            <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                                Send Screening Reminder
                                            </div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                Nudge candidates who have not yet started or completed their screening.
                                            </div>
                                        </div>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        onClick={debouncedOnResendInvitation}
                                        disabled={isResendInvitationDisabled || isAssessmentActionBlocked}
                                        className="flex items-start gap-3 p-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <div className="flex-1">
                                            <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                                Resend Screening Invitation
                                            </div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                Send a new screening link to candidates whose link has expired.
                                            </div>
                                        </div>
                                    </DropdownMenuItem>
                                    {!isNotLinkExpired && (
                                        <DropdownMenuItem
                                            onClick={debouncedOnRequestRetake}
                                            disabled={isRetakeDisabled || isAssessmentActionBlocked}
                                            className="flex items-start gap-3 p-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <div className="flex-1">
                                                <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                                    Request Interview Retake
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                    Ask candidates to retake partial or aborted interviews.
                                                </div>
                                            </div>
                                        </DropdownMenuItem>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="default"
                                    size="sm"
                                    className="gap-2 bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 text-white font-medium transition-all h-8 px-4 rounded-md shadow-sm"
                                >
                                    Reject Candidate{suffix}
                                    <ChevronDownIcon className="w-4 h-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-72">
                                <DropdownMenuItem
                                    onClick={debouncedOnReject}
                                    disabled={hasApplicantWithoutEmail}
                                    className="flex items-start gap-3 p-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <div className="flex-1">
                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                            Send Rejection Email
                                        </div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            {hasApplicantWithoutEmail
                                                ? "Some selected candidates do not have an email address."
                                                : "Notify candidates they were not selected for this role. Candidate moves to the Closed stage."}
                                        </div>
                                    </div>
                                </DropdownMenuItem>
                                {isWaHiringFlow && (
                                    <DropdownMenuItem
                                        onClick={debouncedOnRejectByWa}
                                        className="flex items-start gap-3 p-3 cursor-pointer"
                                    >
                                        <div className="flex-1">
                                            <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Reject by WhatsApp</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Notify candidates over WhatsApp using the approved rejection template. Candidates move to the Closed stage.</div>
                                        </div>
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                    onClick={debouncedOnMarkAsArchived}
                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                >
                                    <div className="flex-1">
                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                            Mark as Archived
                                        </div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            Candidate moves to the Closed stage without email notification.
                                        </div>
                                    </div>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </>
                );

            case "to_review":
                return (
                    <>
                        {/* Update Candidate Status - outline, left */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="gap-2 bg-white hover:bg-gray-100 dark:bg-transparent dark:hover:bg-gray-800 text-black dark:text-gray-100 font-medium transition-all h-8 px-4 rounded-md border border-gray-300 dark:border-gray-600"
                                >
                                    Update Candidate Status
                                    <ChevronDownIcon className="w-4 h-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-72">
                                <DropdownMenuLabel className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-3 pt-3 pb-1">Within This Stage</DropdownMenuLabel>
                                {!allSelectedHaveStatus("Review Scheduled") && (
                                    <DropdownMenuItem
                                        onClick={debouncedOnMarkAsReviewScheduled}
                                        className="flex items-start gap-3 p-3 cursor-pointer"
                                    >
                                        <div className="flex-1">
                                            <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as Review Scheduled</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">A follow-up review or interview session has been lined up.</div>
                                        </div>
                                    </DropdownMenuItem>
                                )}
                                {!allSelectedHaveStatus("Shortlisted") && (
                                    <DropdownMenuItem
                                        onClick={debouncedOnMarkAsShortlisted}
                                        className="flex items-start gap-3 p-3 cursor-pointer"
                                    >
                                        <div className="flex-1">
                                            <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as Shortlisted</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Candidate is a strong hire. Ready for a job offer.</div>
                                        </div>
                                    </DropdownMenuItem>
                                )}
                                {!allSelectedHaveStatus("Pending Decision") && (
                                    <DropdownMenuItem
                                        onClick={debouncedOnMarkAsPendingDecision}
                                        className="flex items-start gap-3 p-3 cursor-pointer"
                                    >
                                        <div className="flex-1">
                                            <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as Pending Decision</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Review completed. Team is still deliberating on next steps.</div>
                                        </div>
                                    </DropdownMenuItem>
                                )}
                                {!allSelectedHaveStatus("Not Suitable") && (
                                    <DropdownMenuItem
                                        onClick={debouncedOnMarkAsNotSuitable}
                                        className="flex items-start gap-3 p-3 cursor-pointer"
                                    >
                                        <div className="flex-1">
                                            <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as Not Suitable</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Does not meet requirements for this role.</div>
                                        </div>
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuLabel className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-3 pt-5 pb-1">Move to Another Stage</DropdownMenuLabel>
                                <DropdownMenuItem
                                    onClick={debouncedOnMarkAsOfferSent}
                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                >
                                    <div className="flex-1">
                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as Offer Sent</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Record that an offer was sent outside the platform. Candidate moves to the Offers stage.</div>
                                    </div>
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={debouncedOnMarkAsHired}
                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                >
                                    <div className="flex-1">
                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as Hired</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Confirm hire without sending a job offer email. Candidate moves to the Closed stage.</div>
                                    </div>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        {/* Email Candidate - blue, middle */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="default"
                                    size="sm"
                                    disabled={hasApplicantWithoutEmail}
                                    title={hasApplicantWithoutEmail ? "Some selected candidates do not have an email address." : undefined}
                                    className="gap-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium transition-all h-8 px-4 rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Email Candidate{suffix}
                                    <ChevronDownIcon className="w-4 h-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-72">
                                <DropdownMenuItem
                                    onClick={debouncedOnSendInterview}
                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                >
                                    <div className="flex-1">
                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Send Review Invitation</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Invite candidates to a follow-up review or interview session.</div>
                                    </div>
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={debouncedOnHire}
                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                >
                                    <div className="flex-1">
                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Send Job Offer Email</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Send a job offer email to candidates. They will move to the Offers stage.</div>
                                    </div>
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={debouncedOnRequestRetake}
                                    disabled={isAssessmentActionBlocked}
                                    className="flex items-start gap-3 p-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <div className="flex-1">
                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Request Interview Retake</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Request the candidate to retake their AI interview. Candidate moves back to the Screening in Progress stage.</div>
                                    </div>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        {/* Reject Candidate - red, right */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="default"
                                    size="sm"
                                    className="gap-2 bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 text-white font-medium transition-all h-8 px-4 rounded-md shadow-sm"
                                >
                                    Reject Candidate{suffix}
                                    <ChevronDownIcon className="w-4 h-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-72">
                                <DropdownMenuItem
                                    onClick={debouncedOnReject}
                                    disabled={hasApplicantWithoutEmail}
                                    className="flex items-start gap-3 p-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <div className="flex-1">
                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Send Rejection Email</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            {hasApplicantWithoutEmail
                                                ? "Some selected candidates do not have an email address."
                                                : "Notify candidates they were not selected for this role. Candidate moves to the Closed stage."}
                                        </div>
                                    </div>
                                </DropdownMenuItem>
                                {isWaHiringFlow && (
                                    <DropdownMenuItem
                                        onClick={debouncedOnRejectByWa}
                                        className="flex items-start gap-3 p-3 cursor-pointer"
                                    >
                                        <div className="flex-1">
                                            <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Reject by WhatsApp</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Notify candidates over WhatsApp using the approved rejection template. Candidates move to the Closed stage.</div>
                                        </div>
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                    onClick={debouncedOnMarkAsArchived}
                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                >
                                    <div className="flex-1">
                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as Archived</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Candidate moves to the Closed stage without email notification.</div>
                                    </div>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </>
                );

            case "offers":
                return (
                    <>
                        {/* Update Candidate Status - outline, left */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="gap-2 bg-white hover:bg-gray-100 dark:bg-transparent dark:hover:bg-gray-800 text-black dark:text-gray-100 font-medium transition-all h-8 px-4 rounded-md border border-gray-300 dark:border-gray-600"
                                >
                                    Update Candidate Status
                                    <ChevronDownIcon className="w-4 h-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-72">
                                <DropdownMenuLabel className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-3 pt-3 pb-1">Within This Stage</DropdownMenuLabel>
                                {!allSelectedHaveStatus("Offer Resent") && (
                                    <DropdownMenuItem
                                        onClick={debouncedOnMarkAsOfferResent}
                                        className="flex items-start gap-3 p-3 cursor-pointer"
                                    >
                                        <div className="flex-1">
                                            <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as Offer Resent</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Record that the offer was sent again.</div>
                                        </div>
                                    </DropdownMenuItem>
                                )}
                                {!allSelectedHaveStatus("Offer Declined") && (
                                    <DropdownMenuItem
                                        onClick={debouncedOnMarkAsOfferDeclined}
                                        className="flex items-start gap-3 p-3 cursor-pointer"
                                    >
                                        <div className="flex-1">
                                            <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as Offer Declined</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Candidate has turned down the offer.</div>
                                        </div>
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuLabel className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-3 pt-5 pb-1">Move to Another Stage</DropdownMenuLabel>
                                <DropdownMenuItem
                                    onClick={debouncedOnMarkAsHired}
                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                >
                                    <div className="flex-1">
                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as Hired</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Confirm hire without sending a job offer email. Candidate moves to the Closed stage.</div>
                                    </div>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        {/* Resend Job Offer - blue, middle */}
                        <Button
                            variant="default"
                            size="sm"
                            onClick={debouncedOnHire}
                            className="gap-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium transition-all h-8 px-4 rounded-md shadow-sm"
                        >
                            Resend Job Offer Email
                        </Button>
                        {/* Reject Candidate - red, right */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="default"
                                    size="sm"
                                    className="gap-2 bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 text-white font-medium transition-all h-8 px-4 rounded-md shadow-sm"
                                >
                                    Reject Candidate{suffix}
                                    <ChevronDownIcon className="w-4 h-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-72">
                                <DropdownMenuItem
                                    onClick={debouncedOnReject}
                                    disabled={hasApplicantWithoutEmail}
                                    className="flex items-start gap-3 p-3 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <div className="flex-1">
                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Send Rejection Email</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            {hasApplicantWithoutEmail
                                                ? "Some selected candidates do not have an email address."
                                                : "Notify candidates they were not selected for this role. Candidate moves to the Closed stage."}
                                        </div>
                                    </div>
                                </DropdownMenuItem>
                                {isWaHiringFlow && (
                                    <DropdownMenuItem
                                        onClick={debouncedOnRejectByWa}
                                        className="flex items-start gap-3 p-3 cursor-pointer"
                                    >
                                        <div className="flex-1">
                                            <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Reject by WhatsApp</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Notify candidates over WhatsApp using the approved rejection template. Candidates move to the Closed stage.</div>
                                        </div>
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                    onClick={debouncedOnMarkAsArchived}
                                    className="flex items-start gap-3 p-3 cursor-pointer"
                                >
                                    <div className="flex-1">
                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Mark as Archived</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Candidate moves to the Closed stage without email notification.</div>
                                    </div>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </>
                );

            case "closed":
                // No actions for closed stage
                return (
                    <Tooltip content={(
                        <p>Move archived candidates to their previous stage. No actions or emails will be triggered.</p>
                    )}>
                        <Button
                            variant="default"
                            size="sm"
                            onClick={debouncedOnMarkAsMoved}
                            className="gap-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium transition-all h-8 px-4 rounded-md shadow-sm"
                        >
                            Move Candidate{suffix}
                        </Button>
                    </Tooltip>
                );

            default:
                return null;
        }
    };

    if (selectedCount === 0) return null;

    return (
        <TooltipProvider>
            <div
                title={maintenance.tooltip
                    ?? (isWorkspaceRestricted ? "Your trial has expired. Upgrade to use bulk actions." : undefined)}
                className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-40 bg-gray-50 dark:bg-gray-900/90 rounded-full shadow-lg px-4 py-2 flex items-center gap-3 border border-gray-200 dark:border-gray-700"
                style={{
                    paddingTop: "16px",
                    paddingRight: "32px",
                    paddingBottom: "16px",
                    paddingLeft: "32px",
                }}
            >
                <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {selectedCount === 1
                            ? "1 candidate"
                            : `${selectedCount} candidates`}{" "}
                        selected
                    </span>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClearSelection}
                        className="h-6 w-6 p-0 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-all rounded-md"
                    >
                        <XIcon className="w-3.5 h-3.5" />
                    </Button>
                </div>

                {/* Keep the selection counter + Clear (X) interactive, but make
                    every action button inert — for an expired trial, and now for
                    a maintenance window too (#3573). Gating the row blocks all
                    children at once, so we don't have to thread `disabled`
                    through every DropdownMenuTrigger/Button inside
                    renderCTAButtons.

                    The reason lives on the pill itself rather than here: an
                    inert subtree cannot surface a `title`, so a tooltip on this
                    div would never be readable. */}
                <div
                    className={`flex items-center gap-3 ${isWorkspaceRestricted ? "pointer-events-none opacity-50" : ""} ${maintenance.groupClass}`}
                    aria-disabled={isWorkspaceRestricted}
                    {...maintenance.groupProps}
                >
                    {/* CTA Buttons based on current stage */}
                    {renderCTAButtons() && (
                        <>
                            <div className="h-5 w-px bg-gray-200 dark:bg-gray-700" />

                            <div className="flex items-center gap-2">{renderCTAButtons()}</div>
                        </>
                    )}

                    {/* Delete button */}
                    {onDelete && (
                        <>
                            <div className="h-5 w-px bg-gray-200 dark:bg-gray-700" />
                            <Tooltip content={<p>{isDeleting ? "Deleting candidates…" : "Delete selected candidates"}</p>}>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={onDelete}
                                    disabled={isWorkspaceRestricted || maintenance.blocked || isDeleting}
                                    className="h-8 w-8 p-0 border-gray-300 dark:border-gray-600 text-gray-500 hover:text-red-500 hover:border-red-300 hover:bg-red-50 dark:hover:bg-red-950/50 transition-all rounded-md disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {isDeleting ? (
                                        <Loader2Icon className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Trash2Icon className="w-4 h-4" />
                                    )}
                                </Button>
                            </Tooltip>
                        </>
                    )}
                </div>
            </div>
        </TooltipProvider>
    );
}
